import React, { useState } from "react";
import { Plus } from "lucide-react";

import PageWrapper from "../../../../components/layout/page/PageWrapper";
import PageHeader from "../../../../components/layout/page/PageHeader";

import Heading from "../../../../components/typography/Heading";
import Text from "../../../../components/typography/Text";

import SecondaryPillTabs from "../../../../components/tabs/SecondaryPillTabs";
import Button from "../../../../components/buttons/Button";
import { EllipsisMenu } from "../../../../components/menu/ellipsis/EllipsisMenu";
import EmptyState from "../../../../components/feedback/EmptyState";
import ContentPanel from "../../../../components/layout/content/ContentPanel";
import FormModal from "../../../../components/modals/FormModal";

import TalentAvatar from "../../components/avatar/TalentAvatar";
import { useUserTalent } from "../hooks/useUserTalent";

import TalentOverviewView from "../views/TalentOverviewView";
import TalentSportsView from "../views/TalentSportsView";
import TalentExperiencesView from "../views/TalentExperiencesView";
import TalentAchievementsView from "../views/TalentAchievementsView";
import TalentMediaView from "../views/TalentMediaView";
import PrivateTalentPulseView from "../pulse/views/PrivateTalentPulseView";
import TalentDetailsView from "../views/TalentDetailsView";

import TalentProfileForm from "../forms/TalentProfileForm";
import TalentSportForm from "../forms/TalentSportForm";
import TalentExperienceForm from "../forms/TalentExperienceForm";
import TalentAchievementForm from "../forms/TalentAchievementForm";
import TalentMediaForm from "../forms/TalentMediaForm";
import TalentProfileUploadForm from "../forms/uploads/TalentProfileUploadForm";

import "./PrivateTalentPage.css";

export default function PrivateTalentPage({ readOnly = false }) {
  const talent = useUserTalent();

  const [activeTab, setActiveTab] = useState("overview");
  const [editor, setEditor] = useState(null);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);

  const { profile, loading, saving, error, refresh } = talent;

  const sports = Array.isArray(profile?.sports) ? profile.sports : [];

  const experiences = Array.isArray(profile?.experiences)
    ? profile.experiences
    : [];

  const achievements = Array.isArray(profile?.achievements)
    ? profile.achievements
    : [];

  const media = Array.isArray(profile?.media) ? profile.media : [];

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "sports", label: `Sports (${sports.length})` },
    {
      id: "experiences",
      label: `Experience (${experiences.length})`,
    },
    {
      id: "achievements",
      label: `Achievements (${achievements.length})`,
    },
    { id: "media", label: `Media (${media.length})` },
  ];

  const openEditor = (kind, initial = null) => {
    if (readOnly || saving) return;

    setEditor({ kind, initial });
  };

  const closeEditor = () => {
    if (!saving) setEditor(null);
  };

  const handleSave = async (action, data) => {
    await action(data);
    setEditor(null);
  };

  const handleDelete = async (description, action) => {
    if (readOnly || saving) return;

    const confirmed = window.confirm(
      `Delete ${description}? This action cannot be undone.`,
    );

    if (!confirmed) return;

    try {
      await action();
    } catch (error) {
      console.error("Delete Talent ID record failed:", error);
    }
  };

  const handleSetPrimary = async (sportId) => {
    if (readOnly || saving) return;

    try {
      await talent.setPrimarySport(sportId);
    } catch (error) {
      console.error("Update primary sport failed:", error);
    }
  };

  const openPhotoUpload = () => {
    if (readOnly || saving || !profile?.talentId) return;

    setShowPhotoUpload(true);
  };

  const renderView = () => {
    switch (activeTab) {
      case "sports":
        return (
          <TalentSportsView
            sports={sports}
            busy={saving}
            readOnly={readOnly}
            onAdd={() => openEditor("sport")}
            onEdit={(item) => openEditor("sport", item)}
            onPrimary={(item) => handleSetPrimary(item.sportId)}
            onRemove={(item) =>
              handleDelete(item.sport, () => talent.removeSport(item.sportId))
            }
          />
        );

      case "experiences":
        return (
          <TalentExperiencesView
            experiences={experiences}
            busy={saving}
            readOnly={readOnly}
            onAdd={() => openEditor("experience")}
            onEdit={(item) => openEditor("experience", item)}
            onRemove={(item) =>
              handleDelete(item.title, () =>
                talent.removeExperience(item.experienceId),
              )
            }
          />
        );

      case "achievements":
        return (
          <TalentAchievementsView
            achievements={achievements}
            busy={saving}
            readOnly={readOnly}
            onAdd={() => openEditor("achievement")}
            onEdit={(item) => openEditor("achievement", item)}
            onRemove={(item) =>
              handleDelete(item.title, () =>
                talent.removeAchievement(item.achievementId),
              )
            }
          />
        );

      case "media":
        return (
          <TalentMediaView
            media={media}
            busy={saving}
            readOnly={readOnly}
            onAdd={() => openEditor("media")}
            onEdit={(item) => openEditor("media", item)}
            onRemove={(item) =>
              handleDelete(item.title, () => talent.removeMedia(item.mediaId))
            }
          />
        );

      default:
        return (
          <div className="user-talent__overview-layout">
            <div className="user-talent__overview-main">
              <TalentOverviewView
                profile={profile}
                readOnly={readOnly}
                onEdit={() => openEditor("profile", profile)}
              />

              {profile?.talentId && (
                <PrivateTalentPulseView
                  talentId={profile.talentId}
                  talentTag={profile.talentTag}
                  sports={sports}
                  readOnly={readOnly}
                />
              )}
            </div>

            <aside className="user-talent__overview-aside">
              <TalentDetailsView profile={profile} />
            </aside>
          </div>
        );
    }
  };

  const renderEditor = () => {
    if (!editor || readOnly) return null;

    const { kind, initial } = editor;

    const commonProps = {
      initial,
      saving,
      onClose: closeEditor,
    };

    switch (kind) {
      case "profile":
        return (
          <TalentProfileForm
            {...commonProps}
            onSave={(data) =>
              handleSave(initial ? talent.update : talent.create, data)
            }
          />
        );

      case "sport":
        return (
          <TalentSportForm
            {...commonProps}
            onSave={(data) =>
              handleSave(
                initial
                  ? (payload) => talent.updateSport(initial.sportId, payload)
                  : talent.addSport,
                data,
              )
            }
          />
        );

      case "experience":
        return (
          <TalentExperienceForm
            {...commonProps}
            sports={sports}
            onSave={(data) =>
              handleSave(
                initial
                  ? (payload) =>
                      talent.updateExperience(initial.experienceId, payload)
                  : talent.addExperience,
                data,
              )
            }
          />
        );

      case "achievement":
        return (
          <TalentAchievementForm
            {...commonProps}
            sports={sports}
            onSave={(data) =>
              handleSave(
                initial
                  ? (payload) =>
                      talent.updateAchievement(initial.achievementId, payload)
                  : talent.addAchievement,
                data,
              )
            }
          />
        );

      case "media":
        return (
          <TalentMediaForm
            {...commonProps}
            sports={sports}
            onSave={(data) =>
              handleSave(
                initial
                  ? (payload) => talent.updateMedia(initial.mediaId, payload)
                  : talent.addMedia,
                data,
              )
            }
          />
        );

      default:
        return null;
    }
  };

  const showCreate = !readOnly && !loading && !profile && !error;

  return (
    <PageWrapper>
      <div className="user-talent">
        <PageHeader
          title="My Talent ID"
          description="Manage your sporting identity, experience, achievements and media."
          actions={
            showCreate && (
              <Button
                value="Create Talent ID"
                icon={<Plus size={17} />}
                disabled={saving}
                onClick={() => openEditor("profile")}
              />
            )
          }
        />

        {error && (
          <div className="user-talent__error" role="alert">
            <Text as="span">{error}</Text>

            <Button
              value="Retry"
              variant="primary-outlined"
              disabled={loading || saving}
              onClick={refresh}
            />
          </div>
        )}

        {loading && !profile ? (
          <Text as="p" tone="secondary" size="sm">
            Loading Talent ID...
          </Text>
        ) : !profile && !error ? (
          <EmptyState
            title="Create your Talent ID"
            description="Build your sporting profile and share your experience, achievements and highlights."
          />
        ) : profile ? (
          <>
            <ContentPanel className="user-talent__hero">
              <TalentAvatar
                profile={profile}
                size="large"
                onUpload={readOnly ? undefined : openPhotoUpload}
                disabled={saving}
              />

              <div className="user-talent__identity">
                <Text as="span" className="user-talent__eyebrow">
                  MY TALENT ID
                </Text>

                <Heading as="h2" size="lg">
                  {profile.name || `Talent ID #${profile.talentId}`}
                </Heading>

                <Text as="p" tone="secondary" size="sm">
                  {[profile.talentTag, profile.visibility || "Private"]
                    .filter(Boolean)
                    .join(" · ")}
                </Text>
              </div>

              {!readOnly && (
                <EllipsisMenu
                  ariaLabel="Talent ID options"
                  position="right"
                  disabled={saving}
                  options={[
                    {
                      id: "edit",
                      label: "Edit Profile",
                      action: () => openEditor("profile", profile),
                    },
                    {
                      id: "delete",
                      label: "Delete Talent ID",
                      className: "is-danger",
                      action: () => handleDelete("Talent ID", talent.remove),
                    },
                  ]}
                />
              )}
            </ContentPanel>

            <div className="user-talent__tabs">
              <SecondaryPillTabs
                tabs={tabs}
                activeTab={activeTab}
                onChange={setActiveTab}
              />
            </div>

            <div className="user-talent__content">{renderView()}</div>
          </>
        ) : null}

        {renderEditor()}

        <FormModal
          isOpen={Boolean(showPhotoUpload && profile && !readOnly)}
          onClose={() => setShowPhotoUpload(false)}
        >
          {profile && (
            <>
              <TalentProfileUploadForm
                talentId={profile.talentId}
                onClose={() => setShowPhotoUpload(false)}
                onRefresh={refresh}
              />
            </>
          )}
        </FormModal>
      </div>
    </PageWrapper>
  );
}
